// utils/indexedDB.js

const DB_NAME = "AudioBooksDB";
const DB_VERSION = 1;
const STORE_NAME = "savedAudio";

let db = null;

const openDB = () => {
    return new Promise((resolve, reject) => {
        if (db) {
            resolve(db);
            return;
        }

        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
            const currentDb = event.target.result;
            if (!currentDb.objectStoreNames.contains(STORE_NAME)) {
                currentDb.createObjectStore(STORE_NAME, { keyPath: "audioUrl" });
            }
        };

        request.onsuccess = (event) => {
            db = event.target.result;
            resolve(db);
        };

        request.onerror = (event) => {
            console.error("IndexedDB error:", event.target.errorCode);
            reject("Error opening IndexedDB");
        };
    });
};

/**
 * Saves an audio file for offline use in IndexedDB.
 * It fetches the audio, reports download progress, and stores it as a Blob.
 *
 * @param {string} audioUrl - The URL of the audio file to save.
 * @param {string} audioName - The display name of the audio.
 * @param {string} bookId - The ID of the book this audio belongs to.
 * @param {function(number): void} [onProgress] - Optional callback function to report download progress (0-100).
 * @returns {Promise<{success: boolean, message?: string, error?: string}>} - Result of the save operation.
 */
export const saveAudioForOffline = async (audioUrl, audioName, bookId, onProgress) => {
    try {
        const database = await openDB();

        // Check if audio is already saved before fetching
        const transactionCheck = database.transaction(STORE_NAME, "readonly");
        const storeCheck = transactionCheck.objectStore(STORE_NAME);
        const existingAudio = await new Promise((resolve, reject) => {
            const request = storeCheck.get(audioUrl);
            request.onsuccess = (event) => resolve(event.target.result);
            request.onerror = (event) => reject(event.target.error);
        });

        if (existingAudio) {
            // If already exists, report 100% progress and success message
            if (onProgress) onProgress(100);
            return { success: true, message: "Аудио қазірдің өзінде сақталған." }; // Audio already saved.
        }

        // Fetch the audio with progress tracking
        const response = await fetch(audioUrl);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentLength = response.headers.get('Content-Length');
        const total = contentLength ? parseInt(contentLength, 10) : 0;
        let loaded = 0;

        const reader = response.body.getReader();
        const chunks = [];

        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            chunks.push(value);
            loaded += value.length;
            if (onProgress && total > 0) {
                const progress = (loaded / total) * 100;
                onProgress(parseFloat(progress.toFixed(2))); // Report progress
            }
        }

        const audioBlob = new Blob(chunks, { type: response.headers.get('Content-Type') || 'application/octet-stream' });

        // Start the transaction to save the data
        const transaction = database.transaction(STORE_NAME, "readwrite");
        const store = transaction.objectStore(STORE_NAME);

        return new Promise((resolve, reject) => {
            transaction.oncomplete = () => {
                // Ensure progress is 100% on successful completion
                if (onProgress) onProgress(100);
                resolve({ success: true, message: "Аудио офлайн режимде сақталды!" }); // Audio saved offline!
            };

            transaction.onerror = (event) => {
                console.error("Error saving audio to IndexedDB:", event.target.error);
                // Reset progress on error
                if (onProgress) onProgress(0);
                reject({ success: false, error: `Аудионы сақтауда қате пайда болды: ${event.target.error.message || event.target.error}` }); // Error saving audio:
            };

            const dataToStore = {
                audioUrl: audioUrl,
                audioName: audioName,
                bookId: bookId,
                audioBlob: audioBlob,
                savedAt: new Date(),
            };

            // Attempt to add. If audioUrl (keyPath) already exists, it will throw a ConstraintError,
            // but we already handled that with the initial check.
            store.add(dataToStore);
        });

    } catch (error) {
        console.error("Error in saveAudioForOffline:", error);
        // Reset progress on any initial error (e.g., fetch error)
        if (onProgress) onProgress(0);
        return { success: false, error: error.message || "Белгісіз қате." }; // Unknown error.
    }
};

export const getSavedAudio = async (audioUrl) => {
    try {
        const database = await openDB();
        const transaction = database.transaction(STORE_NAME, "readonly");
        const store = transaction.objectStore(STORE_NAME);

        return new Promise((resolve, reject) => {
            const request = store.get(audioUrl);

            request.onsuccess = (event) => {
                resolve(event.target.result);
            };

            request.onerror = (event) => {
                console.error("Error getting audio from IndexedDB:", event.target.error);
                reject(null);
            };
        });
    } catch (error) {
        console.error("Error in getSavedAudio:", error);
        return null;
    }
};

export const getAllSavedAudio = async () => {
    try {
        const database = await openDB();
        const transaction = database.transaction(STORE_NAME, "readonly");
        const store = transaction.objectStore(STORE_NAME);

        return new Promise((resolve, reject) => {
            const request = store.getAll();

            request.onsuccess = (event) => {
                resolve(event.target.result);
            };

            request.onerror = (event) => {
                console.error("Error getting all audio from IndexedDB:", event.target.error);
                reject([]);
            };
        });
    } catch (error) {
        console.error("Error in getAllSavedAudio:", error);
        return [];
    }
};

export const deleteSavedAudio = async (audioUrl) => {
    try {
        const database = await openDB();
        const transaction = database.transaction(STORE_NAME, "readwrite");
        const store = transaction.objectStore(STORE_NAME);

        return new Promise((resolve, reject) => {
            const request = store.delete(audioUrl);

            request.onsuccess = () => {
                resolve({ success: true, message: "Аудио жойылды." }); // Audio deleted.
            };

            request.onerror = (event) => {
                console.error("Error deleting audio from IndexedDB:", event.target.error);
                reject({ success: false, error: `Аудионы жоюда қате пайда болды: ${event.target.error.message || event.target.error}` }); // Error deleting audio:
            };
        });
    } catch (error) {
        console.error("Error in deleteSavedAudio:", error);
        return { success: false, error: error.message || "Белгісіз қате." }; // Unknown error.
    }
};